/**
 * The certification-day spreadsheet import (API migration 0357).
 *
 * Declared HERE rather than inside the screen that renders it. An equipment
 * shape declared locally in `EquipmentScreen.tsx` is on file in CLAUDE.md as the
 * shape that let raw snake_case be read straight off the wire; a payload this
 * screen sends to a write endpoint has more to lose from the same mistake.
 *
 * ── THESE ARE NOT VERIFIED BY THE COMPILER, AND THAT IS THE RISK ────────────
 *
 * `apiClient` has one response generic and 47 uncoupled payload interfaces
 * behind it, so a field the API stops sending stays declared here forever and
 * reads as `undefined` on screen. Everything below was read off the live
 * handlers in `equipment.routes.ts` and `cert-day-import.service.ts`, not
 * inferred from the UI's needs. The coupling that actually holds is the test
 * fixture, not this file.
 */

/** Which column holds what. Indices into a row, chosen by the operator. */
export interface CertDayImportMapping {
  serial: number | null;
  days: number | null;
  workDate: number | null;
  rigName?: number | null;
  customerName?: number | null;
  note?: number | null;
}

/** What the operator settled on a row that the serial could not. */
export interface CertDayImportResolution {
  equipmentId?: string | null;
  workDate?: string | null;
}

/**
 * The body for BOTH preview and confirm. Identical on purpose — the server
 * rebuilds the preview from it before writing, so a commit that differed from
 * the preview by so much as a field would be committing something the operator
 * never saw.
 */
export interface CertDaysImportRequest {
  rows: (string | null)[][];
  /** The header the upload step returned. The mapping's indices refer to it. */
  columns: string[];
  mapping: CertDayImportMapping;
  /** Keyed by 1-based row number, as a string. */
  resolutions?: Record<string, CertDayImportResolution>;
}

export interface CertImportSheet {
  name: string;
  columns: string[];
  rows: (string | null)[][];
}

export interface CertImportUploadResult {
  fileName: string;
  sheets: CertImportSheet[];
  rowCount: number;
  parsedCount: number;
  truncated: boolean;
}

export type CertDayImportRowStatus =
  | 'matched' | 'resolved' | 'ambiguous' | 'unmatched' | 'invalid';

export type CertDayImportProblem =
  | 'serial_missing'
  | 'serial_unmatched'
  | 'serial_ambiguous'
  | 'days_missing'
  | 'days_not_whole'
  | 'days_not_positive'
  | 'work_date_missing'
  | 'work_date_ambiguous'
  | 'work_date_unreadable';

export interface CertImportRosterUnit {
  id: string;
  name: string;
  type: string | null;
  serialNumber: string | null;
}

export interface CertDayImportRow {
  rowNumber: number;
  serialRaw: string | null;
  /** What the serial was normalized to before matching. SHOWN, so the operator
   *  can see what joined to what rather than trusting that something did. */
  serialKey: string | null;
  days: number | null;
  daysRaw: string | null;
  workDate: string | null;
  workDateRaw: string | null;
  rigName: string | null;
  customerName: string | null;
  note: string | null;
  status: CertDayImportRowStatus;
  problems: CertDayImportProblem[];
  equipmentId: string | null;
  equipment: CertImportRosterUnit | null;
  /** Only on `ambiguous` — what the picker is narrowed to. */
  candidates: CertImportRosterUnit[];
  matchMethod: 'serial' | 'manual' | null;
  duplicateOfRows: number[];
}

export interface CertDayCounterProjection {
  equipmentId: string;
  name: string;
  serialNumber: string | null;
  /** NULL means NEVER COUNTED, not zero. Render it as a dash. */
  counterBefore: number | null;
  daysAdded: number;
  counterAfter: number;
  rowNumbers: number[];
}

export interface CertDayImportPreview {
  rows: CertDayImportRow[];
  counters: CertDayCounterProjection[];
  counts: {
    total: number;
    matched: number;
    resolved: number;
    ambiguous: number;
    unmatched: number;
    invalid: number;
    unresolved: number;
    duplicateRows: number;
  };
  /** True only when EVERY row is matched or resolved. The Commit button's gate. */
  committable: boolean;
  /** The picker's options, company-scoped by the same query that decided the
   *  matches — so what can auto-match and what can be picked cannot come apart. */
  roster: CertImportRosterUnit[];
}

export interface CertDayImportCommitResult {
  importBatchId: string;
  inserted: Array<{
    rowNumber: number;
    adjustmentId: string;
    equipmentId: string;
    days: number;
    workDate: string;
    matchMethod: 'serial' | 'manual';
    counterBefore: number | null;
    counterAfter: number;
  }>;
  counts: {
    inserted: number;
    matchedBySerial: number;
    resolvedByHand: number;
    unitsTouched: number;
  };
}

/**
 * What a row's problem means, in the operator's words.
 *
 * An exhaustive map keyed on every value of the union — not a fallback that
 * echoes the raw key. A label map that guesses is how a display bug gets
 * reported as a data bug.
 */
export const CERT_IMPORT_PROBLEM_LABELS: Record<CertDayImportProblem, string> = {
  serial_missing: 'No serial number in this row',
  serial_unmatched: 'No equipment with this serial',
  serial_ambiguous: 'This serial is on more than one unit',
  days_missing: 'No number of days',
  days_not_whole: 'Days must be a whole number',
  days_not_positive: 'Days must be at least 1',
  work_date_missing: 'No work date',
  work_date_ambiguous: 'Could be day/month or month/day',
  work_date_unreadable: 'Not a date this can read',
};

export const CERT_IMPORT_STATUS_LABELS: Record<CertDayImportRowStatus, string> = {
  matched: 'Matched on serial',
  resolved: 'Chosen by you',
  ambiguous: 'Serial on two units',
  unmatched: 'Needs equipment',
  invalid: 'Fix the sheet',
};

/** Which fields the operator must map, and what to call them. */
export const CERT_IMPORT_COLUMNS: Array<{
  key: keyof CertDayImportMapping;
  label: string;
  required: boolean;
  hint: string;
}> = [
  { key: 'serial', label: 'Serial number', required: true, hint: 'Matched against the serial on each unit' },
  { key: 'days', label: 'Days', required: true, hint: 'A whole number, at least 1' },
  { key: 'workDate', label: 'Work date', required: true, hint: 'The day the tool worked' },
  { key: 'rigName', label: 'Rig', required: false, hint: 'Optional — printed beside the entry' },
  { key: 'customerName', label: 'Customer', required: false, hint: 'Optional — printed beside the entry' },
  { key: 'note', label: 'Note', required: false, hint: 'Optional' },
];
